import React, { MutableRefObject, useMemo } from 'react'
import type { Chart as ChartJS } from 'chart.js'
import { useChartExport } from '../../hooks/useChartExport'
import type { FfpDashboardData, MeasureKey, PeriodKey, Trend } from '../../types'
import { formatPercent, guideStatus, lastValue, longMonth, sincePhrase, sliceSeries, trendVerdict } from '../../utils'
import { Card } from '../Card'
import { TrendChart } from '../charts/TrendChart'
import { Reveal } from '../Reveal'
import { cardFooter, excludedChip, MEASURES, STATUS_LABELS } from '../../copy'
import { Note } from '../CardParts'
import { Figure, NoFigures, StatusPill } from './parts'

type Props = {
  measure: MeasureKey
  data: FfpDashboardData
  period: PeriodKey
  highlighted: boolean
  onAbout: () => void
}

// "60% of your people now pay under 10% of their pay towards debt, up from 41% in Feb 2026."
// Always first capture to latest, whatever the period, so it names the month.
const withinGuideNote = (trend: Trend, measure: MeasureKey): string | null => {
  const { first, latest } = trend.within_guide
  if (!latest) return null

  const now = MEASURES[measure].share(latest.share, trend.guide)
  if (!first || first.month === latest.month || first.share === latest.share) return `${now}.`
  return `${now}, ${latest.share > first.share ? 'up' : 'down'} from ${first.share}% in ${longMonth(first.month)}.`
}

export const TrendCard = ({ measure, data, period, highlighted, onAbout }: Props) => {
  const trend = data.health.trends[measure]
  const copy = MEASURES[measure]
  const points = useMemo(() => sliceSeries(trend.series, period), [trend.series, period])
  const latest = lastValue(points)
  const { chartRef, exportImage } = useChartExport<'line'>()
  const verdict = trendVerdict(points, copy.direction)
  // From the first month the chart draws, not the company's first capture.
  const firstShown = points.find((point) => point.value !== null)
  const titles = { improving: copy.title, worsening: copy.worse, steady: `${copy.neutral} ${sincePhrase(firstShown ? firstShown.month : data.first_month)}` }
  const footer = { text: cardFooter(data, points, trend.people), chip: excludedChip(trend.excluded) }

  const body = latest ? <TrendBody measure={measure} trend={trend} points={points} value={latest.value} chartRef={chartRef} /> : <NoFigures />

  return (
    <Reveal>
      <Card question={copy.question} title={titles[verdict]} onAbout={onAbout} highlighted={highlighted} exportImage={latest ? exportImage : undefined} exportName={copy.name} footer={footer}>
        {body}
      </Card>
    </Reveal>
  )
}

type BodyProps = {
  measure: MeasureKey
  trend: Trend
  points: Trend['series']
  // The latest average in the period.
  value: number
  chartRef: MutableRefObject<ChartJS<'line'> | undefined>
}

const TrendBody = ({ measure, trend, points, value, chartRef }: BodyProps) => {
  const copy = MEASURES[measure]
  const status = guideStatus(value, trend.guide, copy.direction)

  return (
    <>
      <Figure value={formatPercent(value)} label={copy.bigLabel}>
        <StatusPill status={status} label={STATUS_LABELS[copy.direction][status]} />
      </Figure>
      <TrendChart
        ref={chartRef}
        points={points}
        guide={trend.guide}
        direction={copy.direction}
        zoneLabel={copy.zoneLabel(trend.guide)}
        label={`${copy.name}, month by month, against the ${trend.guide}% guide`}
      />
      <Note text={withinGuideNote(trend, measure)} />
    </>
  )
}
